import { motion } from "motion/react";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import type { MenuExtraResponse } from "@/api/lib/db/types";
import type {
	CreateMenuExtraInput,
	UpdateMenuExtraInput,
} from "@/api/routes/menu/types";
import Icon from "../../../lib/components/custom/Icon";
import { Badge } from "../../../lib/components/ui/badge";
import { Button } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../lib/components/ui/dialog";
import SearchInput from "../../../lib/components/ui/search-input";
import { useApi } from "../../../lib/hooks/api/use-api";
import { MenuExtraForm } from "./MenuExtraForm";

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.05,
		},
	},
};

const fadeInUp = {
	initial: { opacity: 0, y: 10 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.4, ease: "easeOut" },
};

export function MenuExtrasTab() {
	const [searchTerm, setSearchTerm] = useState("");
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [editingExtra, setEditingExtra] = useState<MenuExtraResponse | null>(
		null,
	);

	const { menu } = useApi();
	const {
		data: extrasData,
		isLoading,
		refetch,
	} = menu.getExtras({ limit: 100 });

	const filteredExtras = useMemo(() => {
		if (!extrasData?.extras) return [];
		if (!searchTerm) return extrasData.extras;
		const lowerTerm = searchTerm.toLowerCase();
		return extrasData.extras.filter(
			(extra) =>
				extra.name.toLowerCase().includes(lowerTerm) ||
				extra.type.toLowerCase().includes(lowerTerm) ||
				(extra.category || "").toLowerCase().includes(lowerTerm),
		);
	}, [extrasData, searchTerm]);

	const handleCreateExtra = async (formData: FormData) => {
		try {
			const pricingRules: any = {};
			const pricingType = formData.get("pricingType") as string;

			if (pricingType === "fixed") {
				const fixedPrice = parseFloat(formData.get("fixedPrice") as string);
				if (!isNaN(fixedPrice)) {
					pricingRules.fixedPrice = fixedPrice;
				}
			} else if (pricingType === "sizeBased") {
				const sizeBased: Record<string, number> = {};
				const sizes = ["klein", "normal", "gross", "pan-pizza", "blech"];
				sizes.forEach((size) => {
					const price = parseFloat(formData.get(`price_${size}`) as string);
					if (!isNaN(price)) {
						sizeBased[size] = price;
					}
				});
				if (Object.keys(sizeBased).length > 0)
					pricingRules.sizeBased = sizeBased;
			}

			const metadata = {
				isVegan: formData.get("isVegan") === "on",
				isVegetarian: formData.get("isVegetarian") === "on",
			};

			const data: CreateMenuExtraInput = {
				name: formData.get("name") as string,
				type: formData.get("type") as string,
				category: formData.get("category") as string,
				pricingRules: JSON.stringify(pricingRules),
				metadata: JSON.stringify(metadata),
				isAvailable: formData.get("isAvailable") === "on",
			};

			await menu.createExtra.mutateAsync(data);
			toast.success("Extra created successfully");
			setIsCreateDialogOpen(false);
			refetch();
		} catch (error) {
			toast.error("Failed to create extra");
		}
	};

	const handleUpdateExtra = async (id: number, formData: FormData) => {
		try {
			const pricingRules: any = {};
			const pricingType = formData.get("pricingType") as string;

			if (pricingType === "fixed") {
				const fixedPrice = parseFloat(formData.get("fixedPrice") as string);
				if (!isNaN(fixedPrice)) pricingRules.fixedPrice = fixedPrice;
			} else if (pricingType === "sizeBased") {
				const sizeBased: Record<string, number> = {};
				const sizes = ["klein", "normal", "gross", "pan-pizza", "blech"];
				sizes.forEach((size) => {
					const price = parseFloat(formData.get(`price_${size}`) as string);
					if (!isNaN(price)) sizeBased[size] = price;
				});
				if (Object.keys(sizeBased).length > 0)
					pricingRules.sizeBased = sizeBased;
			}

			const metadata = {
				isVegan: formData.get("isVegan") === "on",
				isVegetarian: formData.get("isVegetarian") === "on",
			};

			const data: UpdateMenuExtraInput = {
				name: formData.get("name") as string,
				type: formData.get("type") as string,
				category: formData.get("category") as string,
				pricingRules: JSON.stringify(pricingRules),
				metadata: JSON.stringify(metadata),
				isAvailable: formData.get("isAvailable") === "on",
			};

			await menu.updateExtra.mutateAsync({ id, data });
			toast.success("Extra updated successfully");
			setEditingExtra(null);
			refetch();
		} catch (error) {
			toast.error("Failed to update extra");
		}
	};

	const handleDeleteExtra = async (id: number) => {
		if (!confirm("Are you sure you want to delete this extra?")) return;
		try {
			await menu.deleteExtra.mutateAsync(id);
			toast.success("Extra deleted successfully");
			refetch();
		} catch (error) {
			toast.error("Failed to delete extra");
		}
	};

	if (isLoading)
		return (
			<div className="flex items-center justify-center py-20">
				<Icon name="Loader" className="w-8 h-8 animate-spin" />
			</div>
		);

	return (
		<>
			<div className="flex justify-between items-center">
				<div className="w-full max-w-sm">
					<SearchInput
						placeholder="Search extras..."
						onSearchChange={setSearchTerm}
						className="w-full"
					/>
				</div>

				<Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
					<DialogTrigger asChild>
						<Button className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm">
							<Icon name="Plus" className="w-4 h-4 mr-2" />
							Add Extra
						</Button>
					</DialogTrigger>
					<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
						<DialogHeader>
							<DialogTitle>Create New Extra</DialogTitle>
						</DialogHeader>
						<MenuExtraForm onSubmit={handleCreateExtra} />
					</DialogContent>
				</Dialog>
			</div>

			<motion.div
				variants={staggerContainer}
				initial="initial"
				animate="animate"
				className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
			>
				{filteredExtras.length === 0 ? (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="col-span-full flex flex-col items-center justify-center py-20 bg-card rounded-xl border border-dashed border-border/60"
					>
						<Icon
							name="Layers"
							className="w-12 h-12 text-muted-foreground/40 mb-3"
						/>
						<p className="text-muted-foreground">
							No extras found matching your criteria.
						</p>
					</motion.div>
				) : (
					filteredExtras.map((extra) => {
						let pricingDisplay = "Price varies";
						try {
							const rules = JSON.parse(extra.pricingRules || "{}");
							if (rules.fixedPrice !== undefined) {
								pricingDisplay = `€${Number(rules.fixedPrice).toFixed(2)}`;
							} else if (rules.sizeBased) {
								const prices = Object.values(rules.sizeBased)
									.map((p) => `€${Number(p).toFixed(2)}`)
									.join(" - ");
								pricingDisplay = `Size Based: ${prices}`;
							}
						} catch (e) {}

						return (
							<motion.div
								key={extra.id}
								variants={fadeInUp}
								whileHover={{ y: -4, transition: { duration: 0.2 } }}
							>
								<Card className="h-full overflow-hidden hover:shadow-lg hover:border-primary/20 transition-all duration-300 group bg-card border-border/60">
									<div className="p-5 flex flex-col h-full">
										<div className="flex justify-between items-start mb-4">
											<div>
												<h3 className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">
													{extra.name}
												</h3>
												<div className="flex gap-2 mt-2">
													<Badge
														variant="secondary"
														className="text-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-normal capitalize"
													>
														{extra.type}
													</Badge>
													<Badge
														variant="outline"
														className="text-xs px-1.5 py-0.5 rounded-md font-normal text-muted-foreground capitalize"
													>
														{extra.category}
													</Badge>
												</div>
											</div>
											<Badge
												variant={extra.isAvailable ? "outline" : "secondary"}
												className="text-xs"
											>
												{extra.isAvailable ? "Active" : "Inactive"}
											</Badge>
										</div>

										<div className="flex-1 text-sm text-muted-foreground mb-4">
											<div className="bg-muted/30 p-3 rounded-lg border border-border/50 flex flex-col gap-1">
												<span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
													Pricing
												</span>
												<span className="font-semibold text-foreground">
													{pricingDisplay}
												</span>
											</div>
										</div>

										<div className="flex gap-2 pt-4 border-t border-border/40 mt-auto">
											<Dialog>
												<DialogTrigger asChild>
													<Button
														variant="ghost"
														size="sm"
														className="flex-1 hover:bg-primary/5 hover:text-primary transition-colors"
														onClick={() => setEditingExtra(extra)}
													>
														<Icon name="Pencil" className="w-3.5 h-3.5 mr-2" />
														Edit
													</Button>
												</DialogTrigger>
												<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
													<DialogHeader>
														<DialogTitle>Edit Extra</DialogTitle>
													</DialogHeader>
													{editingExtra && (
														<MenuExtraForm
															initialData={editingExtra}
															onSubmit={(formData) =>
																handleUpdateExtra(editingExtra.id, formData)
															}
														/>
													)}
												</DialogContent>
											</Dialog>

											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
												onClick={() => handleDeleteExtra(extra.id)}
											>
												<Icon name="Trash" className="w-3.5 h-3.5" />
											</Button>
										</div>
									</div>
								</Card>
							</motion.div>
						);
					})
				)}
			</motion.div>
		</>
	);
}
